/**
 * Filter options for opportunity management
 * Single source of truth for all filter dropdowns
 * Translation keys found by codebase scanner (static analysis)
 * 
 * IMPORTANT: All options include `defaultValue` for use with <LCommon> components
 */

export interface FilterOption {
  value: string;
  labelKey: string;
  defaultValue: string;
}

export interface SortOption {
  value: string;
  labelKey: string;
  defaultValue: string;
}

export interface ViewOption {
  value: string;
  labelKey: string;
  defaultValue: string;
  subtitleKey: string;
  subtitleDefaultValue: string;
}

// ============================================
// OPPORTUNITY VIEWS (Enterprise-style pattern)
// ============================================
export const OPPORTUNITY_VIEW_OPTIONS: readonly ViewOption[] = [
  { 
    value: "all", 
    labelKey: "pages.opportunities.views.all", 
    defaultValue: "All",
    subtitleKey: "pages.opportunities.views.all_subtitle",
    subtitleDefaultValue: "All opportunities"
  },
  { 
    value: "unread", 
    labelKey: "pages.opportunities.views.unread", 
    defaultValue: "Unread",
    subtitleKey: "pages.opportunities.views.unread_subtitle",
    subtitleDefaultValue: "Awaiting review"
  },
  { 
    value: "qualified", 
    labelKey: "pages.opportunities.views.qualified", 
    defaultValue: "Qualified",
    subtitleKey: "pages.opportunities.views.qualified_subtitle",
    subtitleDefaultValue: "Ready to engage"
  },
  { 
    value: "won", 
    labelKey: "pages.opportunities.views.won", 
    defaultValue: "Won",
    subtitleKey: "pages.opportunities.views.won_subtitle",
    subtitleDefaultValue: "Closed deals"
  },
  { 
    value: "needs_action", 
    labelKey: "pages.opportunities.views.needs_action", 
    defaultValue: "Needs Action",
    subtitleKey: "pages.opportunities.views.needs_action_subtitle",
    subtitleDefaultValue: "Require follow-up"
  },
] as const;

// ============================================
// OPPORTUNITY SORT OPTIONS
// ============================================
export const OPPORTUNITY_SORT_OPTIONS: readonly SortOption[] = [
  { value: "newest", labelKey: "sort.newest_first", defaultValue: "Newest First" },
  { value: "oldest", labelKey: "sort.oldest_first", defaultValue: "Oldest First" },
  { value: "unread", labelKey: "sort.unread_first", defaultValue: "Unread First" },
  { value: "budget_desc", labelKey: "sort.budget_high_to_low", defaultValue: "Budget: High to Low" },
  { value: "budget_asc", labelKey: "sort.budget_low_to_high", defaultValue: "Budget: Low to High" },
] as const;

// ============================================
// FILTER OPTIONS (legacy - for advanced filters)
// ============================================

export const OPPORTUNITY_STATUS_OPTIONS: readonly FilterOption[] = [
  { value: "new", labelKey: "common.status.new", defaultValue: "New" },
  { value: "contacted", labelKey: "common.status.contacted", defaultValue: "Contacted" },
  { value: "qualified", labelKey: "common.status.qualified", defaultValue: "Qualified" },
  { value: "proposal", labelKey: "common.status.proposal", defaultValue: "Proposal" },
  { value: "won", labelKey: "common.status.won", defaultValue: "Won" },
  { value: "lost", labelKey: "common.status.lost", defaultValue: "Lost" },
  { value: "nurture", labelKey: "common.status.nurture", defaultValue: "Nurture" },
] as const;

export const SERVICE_TYPE_OPTIONS: readonly FilterOption[] = [
  { value: "Tax Planning", labelKey: "common.service.tax_planning", defaultValue: "Tax Planning" },
  { value: "International Tax", labelKey: "common.service.international_tax", defaultValue: "International Tax" },
  { value: "Tax Dispute Resolution", labelKey: "common.service.tax_dispute", defaultValue: "Tax Dispute" },
  { value: "Tax Compliance", labelKey: "common.service.tax_compliance", defaultValue: "Tax Compliance" },
  { value: "Transfer Pricing", labelKey: "common.service.transfer_pricing", defaultValue: "Transfer Pricing" },
  { value: "Other", labelKey: "common.service.other", defaultValue: "Other" },
] as const;

export const TIMELINE_OPTIONS: readonly FilterOption[] = [
  { value: "urgent", labelKey: "common.timeline.urgent", defaultValue: "Urgent" },
  { value: "1-2_weeks", labelKey: "common.timeline.1_2_weeks", defaultValue: "1-2 Weeks" },
  { value: "1_month", labelKey: "common.timeline.1_month", defaultValue: "1 Month" },
  { value: "flexible", labelKey: "common.timeline.flexible", defaultValue: "Flexible" },
] as const;

export const BUDGET_RANGE_OPTIONS: readonly FilterOption[] = [
  { value: "under_5k", labelKey: "common.budget.under_5k", defaultValue: "Under $5K" },
  { value: "5k_15k", labelKey: "common.budget.5k_15k", defaultValue: "$5K - $15K" },
  { value: "15k_50k", labelKey: "common.budget.15k_50k", defaultValue: "$15K - $50K" },
  { value: "50k_plus", labelKey: "common.budget.50k_plus", defaultValue: "$50K+" },
] as const;

// Helper to add "All" option for filter dropdowns
export const withAllOption = <T extends FilterOption>(
  options: readonly T[],
  allLabelKey: string,
  allDefaultValue: string
): readonly [FilterOption, ...T[]] => 
  [{ value: "all" as const, labelKey: allLabelKey, defaultValue: allDefaultValue }, ...options];

// Pre-built filter arrays with "All" option
export const OPPORTUNITY_STATUS_FILTER_OPTIONS = withAllOption(OPPORTUNITY_STATUS_OPTIONS, "common.filters.all_statuses", "All Statuses");
export const SERVICE_TYPE_FILTER_OPTIONS = withAllOption(SERVICE_TYPE_OPTIONS, "common.filters.all_services", "All Services");
export const TIMELINE_FILTER_OPTIONS = withAllOption(TIMELINE_OPTIONS, "common.filters.all_timelines", "All Timelines");
export const BUDGET_RANGE_FILTER_OPTIONS = withAllOption(BUDGET_RANGE_OPTIONS, "common.filters.all_budgets", "All Budgets");
